import { useTranslation } from 'react-i18next';

import { PROPERTY_LEVELS } from '@/constants/property-level';
import type { Projects } from '@/features/projects/api/projects';
import { getFinalAreaRoom } from '@/utils/getFinalAreaRoom';
import { getFinalLivingArea } from '@/utils/getFinalLivingArea';
import { getFinalPrice } from '@/utils/getFinalPrice';

import type { Estates } from '../api/estates';
import { EstatesGridCard } from './estate-grid-card';
import type { EstateInfoProps } from './estate-info';
import { EstatesInfiniteScroll } from './estates-infinite-scroll';
import type { EstatesQuerySort } from '../api/estates';

type EstateGridData = Pick<Estates[number], 'id' | 'name' | 'status' | 'thumbnail'> &
  Partial<Pick<Estates[number], 'creator' | 'estateDetails' | 'estateAreasRooms'>> & {
    availability?: Estates[number]['availability'];
    address?: {
      address?: string | null;
      postalCode?: string | null;
      city?: string | null;
    } | null;
    prices?: Projects[number]['prices'] | Estates[number]['prices'] | null;
    areaRooms?: Projects[number]['areaRooms'] | null;
  };

type EstateGridProps = {
  filters?: Record<string, unknown>;
  data: EstateGridData[];
  filterOwn?: boolean;
  infiniteScroll?: boolean;
  isPending?: boolean;
  sort?: EstatesQuerySort;
  type?: 'estate' | 'project' | 'combined';
  btnActionRender?: (props: EstateInfoProps) => JSX.Element;
};

export const EstateGrid: React.FC<EstateGridProps> = ({
  data,
  filters,
  filterOwn,
  infiniteScroll = true,
  isPending = false,
  sort,
  type = 'estate',
  btnActionRender,
}) => {
  const { t } = useTranslation();

  return (
    <div className="mb-[30px] grid grid-cols-1 gap-[27px] lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {!isPending && data.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-500 lg:col-span-2 xl:col-span-3 2xl:col-span-4">
          {t('notMatchingResults')}
        </div>
      ) : null}
      {data.map((estate) => (
        <GridCard key={estate.id} estate={estate} btnActionRender={btnActionRender} />
      ))}
      {infiniteScroll ? (
        type === 'combined' ? (
          <>
            <EstatesInfiniteScroll
              filters={filters}
              filterOwn={filterOwn}
              sort={sort}
              type="estate"
              cardRenderer={(estate) => (
                <GridCard estate={estate} btnActionRender={btnActionRender} />
              )}
            />
            <EstatesInfiniteScroll
              filters={filters}
              filterOwn={filterOwn}
              sort={sort}
              type="project"
              cardRenderer={(estate) => (
                <GridCard estate={estate} btnActionRender={btnActionRender} />
              )}
            />
          </>
        ) : (
          <EstatesInfiniteScroll
            filters={filters}
            filterOwn={filterOwn}
            sort={sort}
            type={type}
            cardRenderer={(estate) => (
              <GridCard estate={estate} btnActionRender={btnActionRender} />
            )}
          />
        )
      ) : null}
    </div>
  );
};

const GridCard = ({
  estate,
  btnActionRender,
}: Pick<EstateGridProps, 'btnActionRender'> & { estate: EstateGridData }) => {
  const price = getFinalPrice(estate);
  const roomsCount = getFinalAreaRoom(estate);
  const livingArea = getFinalLivingArea(estate);
  return (
    <EstatesGridCard
      propertyLevel={'estateDetails' in estate ? PROPERTY_LEVELS.OBJECT : PROPERTY_LEVELS.PROJECT}
      creator={`${estate.creator?.firstName ?? ''} ${estate.creator?.lastName ?? ''}`}
      address={estate.address}
      status={estate.status}
      availability={estate.availability}
      id={estate.id}
      thumbnailId={estate.thumbnail?.mediaId}
      livingArea={livingArea}
      roomsCount={roomsCount}
      name={estate.name}
      price={price}
      btnActionRender={btnActionRender}
    />
  );
};
